import {
  Select,
  TextAreaInput,
  TextInput,
  SelectOptionType,
} from '@octano/global-ui';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useCallback, useState, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import useTextValidationType from '../../../../components/curriculum/hooks/useTextValidationType';
import useValidatorNotes from '../../../../hooks/useValidatorNotes';
import { CreateValidationFields } from '../../types';
import { CourseValidationSubType } from '../../../../types/CourseValidationType';
import { SecureResponseFile } from '../../../../components/AzureFilesUploader/types';
import AzureFilesUploader from '../../../../components/AzureFilesUploader/AzureFilesUploader';
import ValidationFileChip from './ValidationFileChip';
import { useValidations } from '../../../../hooks/useValidations';

interface RAPValidationFormProps {
  readonly?: boolean;
  validationSubTypeOptions?: SelectOptionType[];
}

const RAPValidationForm = ({
  readonly = false,
  validationSubTypeOptions = [],
}: RAPValidationFormProps) => {
  const { texts } = useTextValidationType();
  const { formattertNote } = useValidatorNotes();
  const { msgValidations } = useValidations();

  const { control, watch } = useFormContext<CreateValidationFields>();
  const supplementaryFiles = useFieldArray({
    control,
    name: 'validationSupplementaryFilesIds',
  });

  const [selectedSubType, files] = watch([
    'rap.validationSubType',
    'validationSupplementaryFilesIds',
  ]);

  const [filesState, setFilesState] = useState(0);

  const requiresExamGrade = useMemo(
    () => selectedSubType?.value === CourseValidationSubType.Proficiency,
    [selectedSubType?.value],
  );

  const handleAddFile = useCallback(
    (file: SecureResponseFile) => {
      supplementaryFiles.append(file);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }

    window.open(file?.url, '_blank');
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      supplementaryFiles.remove(index);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  return (
    <Row className="mb-4">
      <Col xs={12}>
        <Select
          name="rap.validationSubType"
          label={texts.rapType.selectLabel}
          options={validationSubTypeOptions}
          control={control}
          disabled={readonly}
          rules={{ required: texts.required }}
        />
      </Col>
      <Col xs={12} md={requiresExamGrade ? 6 : 12}>
        <TextInput
          name="rap.courseGrade"
          label={texts.courseGradeLabel}
          control={control}
          disabled={readonly}
          rules={{
            required: texts.required,
            validate: (value: any) => {
              if (!value) {
                return false;
              }
              return formattertNote(value) !== '' || texts.invalidGrade;
            },
          }}
        />
      </Col>

      {!!requiresExamGrade && (
        <Col xs={12} md={6}>
          <TextInput
            name="rap.examGrade"
            label={texts.examGradeLable}
            control={control}
            disabled={readonly}
            rules={{
              required: texts.required,
              validate: (value: any) => {
                if (!value) {
                  return false;
                }
                return formattertNote(value) !== '' || texts.invalidGrade;
              },
            }}
          />
        </Col>
      )}
      <Col xs={12}>
        <TextAreaInput
          label={texts.description.label}
          name="rap.description"
          placeholder={texts.description.placeholder}
          disabled={readonly}
          rules={{
            maxLength: msgValidations.invalidMaxLength(500),
          }}
          control={control}
        />
      </Col>
      {!!(!readonly || files?.length) && (
        <Col className="mt-4" xs={12}>
          <div
            className="w-full p-4 rounded"
            style={{
              boxShadow: '0px 0px 10px 2px rgba(17,17,26,0.08)',
            }}
            key={filesState.toString()}
          >
            <AzureFilesUploader
              label={texts.uploadFile.label}
              disabled={readonly}
              onUploaded={(next) => handleAddFile(next)}
            />
            {!!files?.length && (
              <div
                className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                style={{ gap: 5 }}
              >
                {files?.map((file, i) => (
                  <ValidationFileChip
                    key={file.id.toString()}
                    value={i}
                    children={`${file.name}.${file.extension}`}
                    onRemove={!readonly ? handleRemoveFile : undefined}
                    onDownload={() => handleDownloadFile(file)}
                  />
                ))}
              </div>
            )}
          </div>
        </Col>
      )}
    </Row>
  );
};

export default RAPValidationForm;
